import _ from "lodash";
/**--external-- */
import React, { useState, useRef, useImperativeHandle } from "react";
import classNames from "classnames";
import { Button } from "@toddle-edu/ds-web";
import {
  AddOutlined,
  DeleteOutlined,
  UpArrowOutlined,
  DownArrowOutlined,
} from "@toddle-edu/ds-icons";
import propTypes from "prop-types";

/**--internal-- */
import I18nHOC from "UIComponents/I18nHOC";
import { scrollIntoView } from "Utils";

/**--relative-- */
import CurriculumOptions from "../CurriculumOptions";
import classes from "./Step3.scss";
import { CURRICULUM_PROGRAMS } from "Tooling/modules/ToolingUtils";
import { getCurriculumProgramOptions } from "./Step3Utils";

const TOTAL_REQUIRED_FIELDS = 6;

const Step3 = props => {
  const {
    t,
    curriculumPrograms,
    toddleBuddies,
    updateCurriculumProgramInputRefs,
    mode,
    customRef,
    addCurriculumProgram,
    globalGradesGroupedByCurriculumProgram,
    organizationId,
    organizationDetails,
    setOrganizationDetails,
  } = props;

  const curriculumProgramNodes = useRef([]);
  const curriculumProgramSectionRefs = useRef([]);

  const updateCurriculumProgramNodes = (ref, index) => {
    curriculumProgramNodes.current[index] = ref;
  };

  const updateCurriculumProgramSectionRefs = (ref, index) => {
    curriculumProgramSectionRefs.current[index] = ref;
  };

  const scrollToFirstErrorSection = firstErrorIndex => {
    const sectionRef = curriculumProgramSectionRefs.current[firstErrorIndex];
    if (sectionRef) {
      scrollIntoView({
        element: sectionRef,
      });
    }
  };

  const validateCurriculumPrograms = () => {
    let totalErrors = 0;
    let firstErrorIndex = -1;

    _.forEach(curriculumProgramNodes.current ?? [], (node, index) => {
      const nodeErrors = node?.isValid?.() ?? 0;
      if (nodeErrors > 0 && firstErrorIndex === -1) {
        firstErrorIndex = index;
      }
      totalErrors += nodeErrors;
    });

    return { totalErrors, firstErrorIndex };
  };

  useImperativeHandle(customRef, () => {
    return {
      isValid: () => {
        const { totalErrors, firstErrorIndex } = validateCurriculumPrograms();
        if (totalErrors > 0 && firstErrorIndex !== -1)
          scrollToFirstErrorSection(firstErrorIndex);

        return totalErrors;
      },
    };
  });

  const [curriculumProgramOptions, setCurriculumProgramOptions] = useState(() =>
    getCurriculumProgramOptions({ curriculumPrograms })
  );

  const deleteCurriculumProgram = (payload, curriculumProgramIndex) => {
    const { type: curriculumProgramType } = curriculumPrograms[
      curriculumProgramIndex
    ];

    /**
     * While deleting curriculum program, add it's type in
     * curriculumOptions
     */
    setCurriculumProgramOptions(curriculumOptions => {
      return _.map(curriculumOptions, ({ value, ...rest }) => {
        if (value === curriculumProgramType) {
          return { value, ...rest, isAvailable: true };
        }

        return { value, ...rest };
      });
    });

    props.deleteCurriculumProgram(payload);
  };

  const updateAvailableCurriculumProgramOptions = ({
    currentCurriculumProgramType,
    newCurriculumProgramType,
  }) => {
    setCurriculumProgramOptions(curriculumProgramOptions => {
      return _.map(curriculumProgramOptions, ({ value, ...rest }) => {
        if (value === newCurriculumProgramType) {
          return {
            value,
            ...rest,
            isAvailable: true,
          };
        }

        if (value === currentCurriculumProgramType) {
          return { value, ...rest, isAvailable: true };
        }

        return { value, ...rest };
      });
    });
  };

  const updateCurriculumProgram = (payload, curriculumProgramIndex) => {
    const newCurriculumProgramType = payload.type;

    const currentCurriculumProgram = curriculumPrograms[curriculumProgramIndex];

    const { type: currentCurriculumProgramType } = currentCurriculumProgram;

    if (newCurriculumProgramType) {
      updateAvailableCurriculumProgramOptions({
        currentCurriculumProgramType,
        newCurriculumProgramType,
      });
    }

    props.updateCurriculumProgram(payload);
  };

  const getAvailableCurriculumProgramOptions = ({ index }) => {
    const availableCurriculumPrograms = _.filter(
      curriculumProgramOptions,
      ({ isAvailable }) => isAvailable
    );

    const { type } = curriculumPrograms[index];

    if (_.isEmpty(type)) {
      return availableCurriculumPrograms;
    }

    const curriculumProgramTypeObject = _.find(
      CURRICULUM_PROGRAMS,
      ({ value: curriculumProgramType }) => type === curriculumProgramType
    );

    return _.uniqBy(
      [...availableCurriculumPrograms, curriculumProgramTypeObject],
      ({ value }) => value
    );
  };

  const titleClasses = classNames("heading-3", classes.title);

  const descriptionClasses = classNames("text-body-2", classes.description);

  const curriculumLabelClasses = classNames(
    "text-label-1",
    classes.curriculumLabel
  );

  const toddleBuddiesOptions = _.map(
    toddleBuddies,
    ({ firstName, lastName, id }) => ({
      label: `${firstName} ${lastName}`,
      value: id,
    })
  );

  const onClickReorder = (givenIndex, direction) => {
    let newIndex = 0;
    if (direction === "down") {
      newIndex = givenIndex + 1;
    } else {
      newIndex = givenIndex - 1;
    }

    // Ensure newIndex stays within bounds of the array
    const newIndexInBounds = Math.max(
      0,
      Math.min(organizationDetails.curriculumPrograms.length - 1, newIndex)
    );

    // Splice the array to shift the element
    const updatedCurriculumPrograms = organizationDetails.curriculumPrograms.slice();
    const [removedElement] = updatedCurriculumPrograms.splice(givenIndex, 1);
    updatedCurriculumPrograms.splice(newIndexInBounds, 0, removedElement);

    const updatedOrganizationDetails = {
      ...organizationDetails,
      curriculumPrograms: updatedCurriculumPrograms,
    };

    setOrganizationDetails(updatedOrganizationDetails);
  };

  const totalCurriculumPrograms = _.size(curriculumPrograms);

  return (
    <div className={classes.container}>
      <header className={classes.header}>
        <div className={classes.headerContent}>
          <h3 className={titleClasses}>
            {t("onBoarding:add_programme_details")}
          </h3>
          <p className={descriptionClasses}>
            {t("onBoarding:add_programme_description")}
          </p>
        </div>
        <Button
          variant={"outlined"}
          size="medium"
          onClick={addCurriculumProgram}
          icon={<AddOutlined size={"xx-small"} />}
        >
          {t("common:add_with_label", { label: t("common:programme") })}
        </Button>
      </header>
      <main className={classes.curriculumContainer}>
        {_.map(curriculumPrograms, (curriculumProgram, index) => {
          const { id } = curriculumProgram;
          const onDeleteButtonClick = () => {
            deleteCurriculumProgram({ id }, index);
          };
          const showUpArrow = index !== 0;
          const showDownArrow = index !== totalCurriculumPrograms - 1;

          return (
            <div
              key={id}
              className={classes.curriculumOptions}
              ref={ref => updateCurriculumProgramSectionRefs(ref, index)}
            >
              <div className={classes.curriculumOptionsHeader}>
                <p className={curriculumLabelClasses}>
                  {t("onBoarding:programme_with_number", {
                    number: index + 1,
                  })}
                </p>
                <div className={classes.buttonContainer}>
                  {showUpArrow ? (
                    <Button
                      onClick={() => onClickReorder(index, "up")}
                      icon={<UpArrowOutlined size="xx-small" />}
                      size="small"
                      variant="plain"
                    />
                  ) : null}
                  {showDownArrow ? (
                    <Button
                      onClick={() => onClickReorder(index, "down")}
                      icon={<DownArrowOutlined size="xx-small" />}
                      size="small"
                      variant="plain"
                    />
                  ) : null}
                  <Button
                    onClick={onDeleteButtonClick}
                    icon={<DeleteOutlined size="xx-small" />}
                    size="small"
                    variant="plain"
                  >
                    {t("common:delete")}
                  </Button>
                </div>
              </div>
              <CurriculumOptions
                curriculumProgram={curriculumProgram}
                updateCurriculumProgram={payload =>
                  updateCurriculumProgram(payload, index)
                }
                curriculumProgramOptions={getAvailableCurriculumProgramOptions({
                  index,
                })}
                toddleBuddiesOptions={toddleBuddiesOptions}
                curriculumProgramIndex={index * TOTAL_REQUIRED_FIELDS}
                updateCurriculumProgramInputRefs={
                  updateCurriculumProgramInputRefs
                }
                customRef={ref => updateCurriculumProgramNodes(ref, index)}
                mode={mode}
                initiallySelectedGlobalGrades={_.map(
                  globalGradesGroupedByCurriculumProgram[id] ?? {},
                  ({ id }) => id
                )}
                organizationId={organizationId}
              />
            </div>
          );
        })}
      </main>
    </div>
  );
};

Step3.propTypes = {
  t: propTypes.func,
  curriculumPrograms: propTypes.array,
  toddleBuddies: propTypes.array,
  updateCurriculumProgramInputRefs: propTypes.func,
  mode: propTypes.string,
  customRef: propTypes.object,
  globalGradesGroupedByCurriculumProgram: propTypes.object,
};

export default I18nHOC({ resource: ["onBoarding", "common"] })(Step3);
